import { useMemo, useState } from 'react';
import type { Geometry } from 'geojson';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  IncidentArea,
  useCreateIncidentArea,
  useDeleteIncidentArea,
  useUpdateIncidentArea,
} from 'components/IAP/hooks/incidentAreaHooks';

import { ICS204_TABLE } from './AssignmentList/assignmentCardStyles';
import IncidentAreaEditDialog, {
  BLANK_INCIDENT_AREA,
  type IncidentAreaDraft,
} from './IncidentAreaEditDialog';

const geometryLabel = (geometry: Geometry): string =>
  geometry.type === 'Point' ? 'Point' : 'Area';

export type IncidentAreasTableProps = {
  incidentId: string;
  areas: IncidentArea[];
  readOnly?: boolean;
};

/**
 * Boxes 5 to 8 of the 215A-CG, the hazard rows.
 *
 * A row carries four fields, so the pencil opens a dialog rather than editing
 * in place. Unlike the 209's lists these rows are their own entities with their
 * own endpoints, so each save is a single create or update rather than a
 * rewrite of the whole list.
 */
const IncidentAreasTable: React.FC<IncidentAreasTableProps> = ({
  incidentId,
  areas,
  readOnly = false,
}) => {
  const createArea = useCreateIncidentArea();
  const updateArea = useUpdateIncidentArea();
  const deleteArea = useDeleteIncidentArea();

  const [search, setSearch] = useState('');
  /** The area the dialog is editing, or 'new' while adding. */
  const [editing, setEditing] = useState<IncidentArea | 'new' | null>(null);

  const visibleAreas = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return areas;
    return areas.filter((area) =>
      [area.areaName, area.hazard, area.mitigations, area.riskGain]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [areas, search]);

  const saveArea = (draft: IncidentAreaDraft) => {
    if (editing === 'new') {
      createArea.mutate({ incidentId, ...draft });
    } else if (editing) {
      updateArea.mutate({ ...editing, ...draft });
    }
    setEditing(null);
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label="Search incident areas"
          placeholder="Search incident areas..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            className="tw:ml-auto"
            onClick={() => setEditing('new')}
          >
            <Plus className="tw:size-4" />
            Add Area
          </Button>
        )}
      </div>

      <Table className={ICS204_TABLE} data-testid="ics215a-incident-areas">
        <TableHeader>
          <TableRow>
            <TableHead>Incident Area</TableHead>
            <TableHead>Hazards / Risks</TableHead>
            <TableHead>Mitigations</TableHead>
            <TableHead>Risk/Gain</TableHead>
            {/* Not a printed column: the 215A has no place for a shape, but the
                map layer does, so the card says which rows carry one. */}
            <TableHead>Geolocation</TableHead>
            {!readOnly && <TableHead className="tw:w-24" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleAreas.length === 0 ? (
            <TableRow>
              <TableCell colSpan={readOnly ? 5 : 6} className="tw:text-center">
                {search
                  ? 'No results!'
                  : 'There are no incident areas yet. Click Add Area to add one.'}
              </TableCell>
            </TableRow>
          ) : (
            visibleAreas.map((area) => (
              <TableRow key={area.areaId}>
                <TableCell>{area.areaName || NULL_VALUE}</TableCell>
                <TableCell>{area.hazard || NULL_VALUE}</TableCell>
                <TableCell>{area.mitigations || NULL_VALUE}</TableCell>
                <TableCell>{area.riskGain || NULL_VALUE}</TableCell>
                <TableCell>
                  {area.geometry ? (
                    <span className="tw:inline-flex tw:items-center tw:gap-1">
                      <MapPin className="tw:size-4" aria-hidden />
                      {geometryLabel(area.geometry)}
                    </span>
                  ) : (
                    NULL_VALUE
                  )}
                </TableCell>
                {!readOnly && (
                  <TableCell>
                    <div className="tw:flex tw:justify-end tw:gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${area.areaName}`}
                        onClick={() => setEditing(area)}
                      >
                        <Pencil className="tw:size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${area.areaName}`}
                        onClick={() =>
                          area.areaId && deleteArea.mutate(area.areaId)
                        }
                      >
                        <Trash2 className="tw:size-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <span className="tw:text-sm tw:text-muted-foreground">
        {visibleAreas.length} area(s)
      </span>

      {!readOnly && editing ? (
        <IncidentAreaEditDialog
          isNewArea={editing === 'new'}
          area={editing === 'new' ? BLANK_INCIDENT_AREA : editing}
          onDismiss={() => setEditing(null)}
          onSave={saveArea}
        />
      ) : null}
    </div>
  );
};

export default IncidentAreasTable;
